/**
 * Temporary screen-scoped design tokens for the rebuilt **Bulk Send** flow.
 *
 * Mirrors `Bulk Send - Templates & Batches.dc.html` (desktop) and
 * `Bulk Send - Mobile Flow.dc.html`, so no component inlines a literal. These
 * get promoted into `src/theme` once every screen is re-skinned.
 *
 * Colour note: the mock draws its accent in the Claude orange `#E1500F`. Per
 * the standing brand rule every one of those renders brand red here, which is
 * what `signer.orange.*` already resolves to — same as the editor's tokens.
 */

import { alpha } from "@mui/material/styles";
import {
  chart,
  inkfree,
  mobile,
  notification,
  signer,
  white,
} from "theme/colors";

/** The raw palette the design draws from, mapped onto the app's own tokens. */
const palette = {
  ink: signer.ink,
  body: signer.text.secondary,
  muted: signer.text.faint,
  label: signer.text.meta,
  faint: mobile.base[100],
  borderLight: signer.border.light,
  borderSoft: signer.border.main,
  borderMain: signer.disabled,
  borderStrong: signer.border.strong,
  surface: signer.surface.muted,
  surfaceHover: signer.surface.hover,
  pill: signer.surface.pill,
  white,
  /** Text, borders, dots and icons. Brand red, per the rule above. */
  accent: signer.orange.main,
  accentHover: signer.orange.dark,
  accentTint: signer.orange.tint,
  accentBorder: signer.orange.border,
  accentSoft: signer.orange.faintest,
  /** Solid CTA fills. */
  ctaBg: inkfree.main,
  ctaBgHover: inkfree.dark,

  /** Status semantics, reused so a batch reads like the rest of the app. */
  success: notification.success.icon,
  successBg: notification.success.bg,
  successText: notification.success.title,
  successBorder: notification.success.border,
  danger: notification.error.icon,
  dangerBg: notification.error.bg,
  dangerText: notification.error.title,
  dangerBorder: notification.error.border,
  warnBg: notification.warning.bg,
  warnText: notification.warning.title,
  warnBorder: notification.warning.border,
  /**
   * Progress-segment fills. Blue and grey come from the chart series, which is
   * where the app already keeps categorical status colours. The amber matches
   * the editor's own local amber — a segment fill, not body text, so it is the
   * mock's brighter shade rather than the darker `notification.warning.icon`
   * used where amber has to carry text contrast.
   */
  segmentDelivered: chart.status.pending,
  segmentQueued: chart.status.fallback,
  amber: "#F5B21E",
} as const;

export const bulkSendTokens = {
  palette,

  /** Top toolbar, shared by every step. Matches the create-request header. */
  header: {
    height: 62,
    paddingX: "20px",
    gap: "14px",
    borderColor: palette.borderLight,
    pillGap: "6px",
    pillPaddingX: "9px",
    pillPaddingY: "5px",
    pillRadius: "999px",
    pillBg: palette.accentTint,
    pillBorder: palette.accentBorder,
    pillFontSize: "11px",
    pillFontWeight: 800,
    pillLetterSpacing: ".03em",
    pillIconSize: 14,
    buttonHeight: 38,
    buttonRadius: "9px",
    buttonFontSize: "13.5px",
    buttonFontWeight: 700,
    previousPaddingX: "18px",
    nextPaddingX: "22px",
  },

  /**
   * Each step sits in the same centred column its counterpart uses in the
   * normal create-request flow, so moving between the two never shifts the
   * content width:
   *   Document   → Upload Document        (1080, 32/40/80)
   *   Fields     → the editor             (full width, its own rails)
   *   Signers    → Approvers & Signers    (1220, 26/40/80)
   *   Recipients → no counterpart; takes the Signers column
   *   Review     → Email configurations   (1220, 36/40/80)
   */
  page: {
    documentMaxWidth: 1080,
    documentPaddingTop: "32px",
    listMaxWidth: 1220,
    listPaddingTop: "26px",
    reviewPaddingTop: "36px",
    paddingBottom: "80px",
    paddingTop: "30px",
    paddingX: "40px",
    titleFontSize: "28px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    titleGap: "6px",
    subFontSize: "13px",
    subColor: palette.muted,
    subMarginBottom: "26px",
  },

  /** The panel shape shared by the template picker and saved lists. */
  templatePicker: {
    width: 576,
    radius: "14px",
    headerPadding: "16px 20px",
    headerGap: "11px",
    headerFontSize: "14px",
    headerFontWeight: 800,
    changeFontSize: "12px",
    noticePadding: "12px 20px",
    noticeGap: "9px",
    noticeBg: palette.surfaceHover,
    noticeTickSize: 17,
    noticeTickGlyph: 10,
    noticeFontSize: "11.5px",
    noticeLineHeight: 1.55,
    listPadding: "16px 20px 6px",
    listGap: "10px",
    cardRadius: "12px",
    cardBorderWidth: "1.5px",
    cardPadding: "14px",
    cardGap: "11px",
    thumbWidth: 34,
    thumbHeight: 42,
    thumbRadius: "5px",
    nameFontSize: "13.5px",
    metaFontSize: "11.5px",
    metaMarginTop: "3px",
    radioSize: 18,
    radioDotSize: 9,
    rolesPaddingTop: "11px",
    rolesLabelFontSize: "10.5px",
    rolesLabelLetterSpacing: ".08em",
    roleGap: "10px",
    roleAvatarSize: 22,
    roleAvatarFontSize: "9px",
    roleNameFontSize: "12.5px",
    roleFieldsFontSize: "11.5px",
    roleOrderFontSize: "11px",
    roleOrderPadding: "3px 9px",
    footerPadding: "14px 20px 18px",
    footerButtonHeight: 42,
    footerButtonRadius: "10px",
    footerButtonFontSize: "13.5px",
  },

  /** Step 2, door B — the editor's left rail, where roles are named. */
  rolesRail: {
    headerPadding: "16px 18px 12px",
    eyebrowFontSize: "11px",
    eyebrowLetterSpacing: ".12em",
    hintFontSize: "11.5px",
    hintLineHeight: 1.6,
    hintMarginTop: "6px",
    listPadding: "14px 14px 0",
    listGap: "9px",
    cardRadius: "11px",
    cardBorderWidth: "1.5px",
    cardPadding: "12px",
    cardGap: "9px",
    badgeSize: 22,
    badgeRadius: "6px",
    badgeFontSize: "10px",
    inputHeight: 30,
    inputRadius: "7px",
    inputPaddingX: "9px",
    inputFontSize: "12.5px",
    removeSize: 22,
    removeIconSize: 10,
    metaGap: "8px",
    orderPillFontSize: "11px",
    orderPillPadding: "3px 9px",
    fieldCountFontSize: "11px",
    arrowWidth: 10,
    arrowHeight: 8,
    addHeight: 38,
    addRadius: "10px",
    addGap: "7px",
    addIconSize: 12,
    addFontSize: "12.5px",
  },

  /** Step 3 — which role varies, which is pinned for the batch. */
  roles: {
    rowsGap: "12px",
    rowRadius: "12px",
    rowBorderWidth: "1.5px",
    rowPadding: "13px 18px",
    rowGap: "16px",
    accentBarWidth: 5,
    accentBarRadius: "3px",
    accentBarMinHeight: 44,
    avatarSize: 30,
    avatarFontSize: "11px",
    nameWidth: 210,
    nameFontSize: "14px",
    nameFontWeight: 800,
    fieldNoteFontSize: "11.5px",
    fieldNoteMarginTop: "2px",
    toggleBg: palette.surface,
    toggleRadius: "9px",
    togglePadding: "3px",
    toggleGap: "2px",
    toggleItemHeight: 30,
    toggleItemPaddingX: "14px",
    toggleItemRadius: "7px",
    toggleFontSize: "12px",
    toggleFontWeight: 700,
    toggleActiveShadow: `0 1px 3px ${alpha(signer.ink, 0.12)}`,
    inputHeight: 38,
    inputRadius: "9px",
    inputBorderWidth: "1.5px",
    inputFontSize: "13.5px",
    inputGap: "10px",
    inputPaddingX: "12px",
    codeInputWidth: 240,
    /**
     * The inline phone is pinned to the row's right edge, so it is given a
     * width rather than left to the shared control's own flex basis — which
     * stopped it short of the fields above it.
     */
    phoneWidth: 240,
    inputNameMinWidth: 110,
    inputEmailMinWidth: 140,
    securityHeight: 36,
    securityRadius: "8px",
    securityPaddingX: "12px",
    securityFontSize: "12.5px",
    securityIconSize: 13,
    securityCaretSize: 10,
    /**
     * Who fills the role, on its own line under the identity — inset to the
     * same left edge as the "Deliver via" strip so the card reads as a column.
     */
    fillPadding: "0 18px 10px 64px",
    fillGap: "12px",
    /** The "Deliver via" strip under a role's own row. */
    deliverPadding: "8px 18px 8px 64px",
    deliverGap: "10px",
    deliverLabelFontSize: "11px",
    deliverLabelLetterSpacing: ".06em",
    channelHeight: 36,
    channelPaddingX: "12px",
    channelRadius: "8px",
    channelGap: "9px",
    channelFontSize: "13px",
    channelBoxSize: 16,
    channelBoxRadius: "4px",
    phoneCodeWidth: 62,
    /** The amber note that shares the "Deliver via" row rather than adding one. */
    noticeGap: "8px",
    noticePadding: "7px 12px",
    noticeRadius: "8px",
    noticeFontSize: "11.5px",
    noticeIconSize: 14,
    orderNotePadding: "7px 18px 9px 64px",
    orderNoteGap: "9px",
    orderNoteIconSize: 13,
    orderNoteFontSize: "11.5px",
    warningMarginTop: "20px",
    warningPadding: "14px 16px",
    warningRadius: "10px",
    warningGap: "11px",
    warningIconSize: 15,
    warningFontSize: "12px",
    warningLineHeight: 1.6,
  },

  /** Step 4 — recipients. */
  recipients: {
    headerGap: "24px",
    headerMarginBottom: "22px",
    downloadHeight: 38,
    downloadRadius: "9px",
    downloadBorderWidth: "1.5px",
    downloadPaddingX: "16px",
    downloadGap: "9px",
    downloadIconSize: 15,
    downloadFontSize: "13px",
    routesGap: "8px",
    routesMarginBottom: "20px",
    routeHeight: 40,
    routePaddingX: "16px",
    routeRadius: "10px",
    routeBorderWidth: "1.5px",
    routeGap: "9px",
    routeIconSize: 15,
    routeFontSize: "13px",
    dropzoneRadius: "14px",
    dropzonePadding: "52px 40px",
    dropzoneGap: "22px",
    dropzoneIconSize: 40,
    dropzoneTitleFontSize: "18px",
    dropzoneTitleFontWeight: 600,
    dropzoneHintFontSize: "12.5px",
    noteMarginTop: "18px",
    notePadding: "13px 16px",
    noteRadius: "10px",
    noteGap: "11px",
    noteIconSize: 15,
    noteFontSize: "11.5px",
    /** The facts strip that opens the step: document, signers, delivery, reminder. */
    factCardRadius: "12px",
    factCardPadding: "16px 18px",
    factColumnGap: "18px",
    factLabelFontSize: "10.5px",
    factLabelLetterSpacing: ".07em",
    factValueFontSize: "14px",
    factValueMarginTop: "4px",
    factDividerMargin: "14px",
    factRolesGap: "10px",
  },

  /**
   * Step 4, typed rows — for the handful of recipients nobody opens a
   * spreadsheet for. The design draws the route pill but not this panel, so
   * the chrome is borrowed from the roles step's inputs and the batch table.
   */
  typedRows: {
    headerFontSize: "11.5px",
    headerLetterSpacing: ".06em",
    headerPaddingY: "10px",
    rowGap: "10px",
    rowPaddingY: "8px",
    numberWidth: 26,
    numberFontSize: "11.5px",
    inputHeight: 38,
    inputRadius: "9px",
    inputFontSize: "13px",
    /**
     * A batch runs to 1000 rows, so the list scrolls inside the card rather
     * than turning the step into a mile of page. Roughly eight rows deep —
     * enough to see the shape of the list and that more follows.
     */
    rowsMaxHeight: 430,
    removeSize: 30,
    removeIconSize: 11,
    addHeight: 38,
    addRadius: "9px",
    addGap: "7px",
    addIconSize: 12,
    addFontSize: "12.5px",
    footerGap: "12px",
    footerPaddingTop: "16px",
    countFontSize: "12px",
  },

  /**
   * Step 4 — the check panel that opens *under* the recipient table, never in
   * place of it. Every number in it is derived from the rows above, so the
   * table, banner, chips, worklist and footer cannot disagree.
   *
   * `panelBg`, `panelBorder` and `invalidBorder` are this screen's own values.
   * The chip colours reuse the palette exactly (`notification.success`,
   * `signer.surface.muted`, `signer.text.meta`); `invalidChipBg` is the one
   * near-duplicate — two points off `notification.error.bg` (#FDECEC) — kept
   * because the spec names it.
   */
  check: {
    panelBg: "#FEF9EF",
    panelBorder: "#F0D9A8",
    panelRadius: "12px",
    panelPadding: "16px 18px",
    panelMarginTop: "18px",
    bannerFontSize: "13.5px",
    bannerFontWeight: 800,
    subFontSize: "12px",
    subMarginTop: "3px",
    chipsGap: "8px",
    chipPadding: "4px 11px",
    chipRadius: "999px",
    chipFontSize: "11.5px",
    chipFontWeight: 700,
    chipBorderWidth: "1px",
    chipCountGap: "5px",
    readyChipBorder: notification.success.border,
    invalidChipBorder: "#F5C6C7",
    blankChipBorder: notification.info.border,
    headIconSize: 17,
    readyChipBg: notification.success.bg,
    readyChipText: notification.success.title,
    invalidChipBg: "#FBECEC",
    invalidChipText: notification.error.title,
    /** Unfinished rather than wrong — the app's amber, not its red. */
    incompleteChipBg: notification.warning.bg,
    incompleteChipText: notification.warning.title,
    incompleteChipBorder: notification.warning.border,
    blankChipBg: signer.surface.muted,
    blankChipText: signer.text.meta,
    headPadding: "14px 18px",
    headGap: "12px",
    /** Rows sit on white inside the amber panel, as the design has them. */
    rowBg: white,
    rowPadding: "11px 18px",
    rowGap: "12px",
    rowLabelWidth: 58,
    rowNameWidth: 190,
    /** The banner's warning triangle. */
    panelIcon: notification.warning.icon,
    rowLabelFontSize: "11.5px",
    rowNameFontSize: "12.5px",
    rowReasonFontSize: "11.5px",
    rowActionFontSize: "11.5px",
    rowActionGap: "14px",
    footerMarginTop: "16px",
    footerGap: "16px",
    footerFontSize: "12px",
    footerButtonHeight: 36,
    footerButtonRadius: "9px",
    /** Inline marks on the offending input in the table itself. */
    invalidBorder: "#E8A8A8",
    invalidMessageFontSize: "11px",
    invalidMessageMarginTop: "4px",
  },

  /** Step 4 — the validation worklist shown in place once the file is read. */
  report: {
    width: 576,
    radius: "14px",
    headerPadding: "18px 22px",
    headerGap: "12px",
    headerIconSize: 32,
    headerIconRadius: "9px",
    headerIconGlyph: 16,
    headerTitleFontSize: "14px",
    headerSubFontSize: "11.5px",
    reuploadFontSize: "12px",
    statsPadding: "16px 22px",
    statsGap: "10px",
    statRadius: "10px",
    statPadding: "11px 13px",
    statNumberFontSize: "19px",
    statLabelFontSize: "11px",
    sectionLabelPadding: "0 22px 8px",
    sectionLabelFontSize: "10.5px",
    rowPadding: "12px 22px",
    rowGap: "12px",
    rowLineWidth: 26,
    rowLineFontSize: "11.5px",
    rowValueFontSize: "12.5px",
    rowMessageFontSize: "11px",
    rowActionFontSize: "11.5px",
    footerPadding: "16px 22px",
    footerFontSize: "12px",
    footerButtonHeight: 36,
    footerButtonRadius: "9px",
    footerButtonFontSize: "12.5px",
  },

  /** Step 5's document card, collapsible settings and live email preview. */
  reviewCards: {
    docPadding: "16px",
    docRadius: "12px",
    docGap: "16px",
    thumbWidth: 72,
    thumbHeight: 92,
    thumbRadius: "6px",
    docTitleFontSize: "17px",
    docMetaFontSize: "12.5px",
    docMetaMarginTop: "4px",
    chipsGap: "8px",
    chipsMarginTop: "12px",
    chipPadding: "4px 10px",
    chipRadius: "999px",
    chipFontSize: "11.5px",
    chipDotSize: 7,
    previewLinkFontSize: "12.5px",
    previewLinkMarginTop: "12px",

    sectionGap: "12px",
    sectionPadding: "14px 16px",
    sectionIconSize: 36,
    sectionIconRadius: "10px",
    sectionIconGlyph: 17,
    sectionTitleFontSize: "15px",
    sectionSubFontSize: "12px",
    sectionBodyPadding: "4px 16px 16px 66px",

    labelFontSize: "12.5px",
    counterFontSize: "11.5px",
    tokenGap: "8px",
    tokenPadding: "5px 10px",
    tokenRadius: "8px",
    tokenFontSize: "11.5px",

    previewRadius: "12px",
    previewHeaderPadding: "12px 14px",
    previewHeaderFontSize: "13px",
    previewRowPillPadding: "3px 9px",
    previewRowPillFontSize: "10.5px",
    previewBodyPadding: "14px",
    previewMetaFontSize: "12.5px",
    previewMetaGap: "5px",
    previewTextFontSize: "13px",
    previewButtonHeight: 38,
    previewButtonRadius: "8px",
    previewNoteFontSize: "11.5px",
    previewFooterPadding: "10px 14px",
    previewCaptionFontSize: "11.5px",
    previewCaptionMarginTop: "10px",
  },

  /** Step 5 — review, with the send panel on the right. */
  review: {
    cardRadius: "14px",
    cardRadiusPhone: "12px",
    bodyPadding: "30px 36px 40px",
    rowsRadius: "11px",
    rowPadding: "15px 18px",
    rowGap: "16px",
    keyWidth: 150,
    keyFontSize: "11.5px",
    keyLetterSpacing: ".04em",
    valueFontSize: "13.5px",
    editFontSize: "12px",
    asideWidth: 360,
    asidePadding: "30px 28px 40px",
    asideLabelFontSize: "11.5px",
    asideLabelLetterSpacing: ".06em",
    countFontSize: "42px",
    countLetterSpacing: "-.03em",
    countGap: "9px",
    countUnitFontSize: "13px",
    summaryMargin: "22px 0 20px",
    summaryPadding: "16px 0",
    summaryGap: "11px",
    summaryFontSize: "12.5px",
    noteRadius: "10px",
    notePadding: "13px 14px",
    noteGap: "10px",
    noteIconSize: 15,
    noteFontSize: "11.5px",
    /** The facts strip that opens the step: document, signers, delivery, reminder. */
    factCardRadius: "12px",
    factCardPadding: "16px 18px",
    factColumnGap: "18px",
    factLabelFontSize: "10.5px",
    factLabelLetterSpacing: ".07em",
    factValueFontSize: "14px",
    factValueMarginTop: "4px",
    factDividerMargin: "14px",
    factRolesGap: "10px",
  },

  /** The batch page — where a send lands, and where the Sent list links to. */
  batch: {
    cardRadius: "14px",
    headerPadding: "18px 22px",
    headerGap: "12px",
    titleFontSize: "15px",
    titleFontWeight: 800,
    subFontSize: "11.5px",
    subMarginTop: "3px",
    pillHeight: 28,
    pillPaddingX: "12px",
    pillRadius: "999px",
    pillGap: "7px",
    pillDotSize: 7,
    pillFontSize: "11.5px",
    pillFontWeight: 800,
    bodyPadding: "18px 22px",
    barHeight: 10,
    barRadius: "999px",
    barMarginBottom: "12px",
    legendGap: "14px 20px",
    legendDotSize: 9,
    legendDotRadius: "3px",
    legendItemGap: "7px",
    legendNumberFontSize: "12px",
    legendLabelFontSize: "12px",
    sectionLabelFontSize: "10.5px",
    sectionLabelLetterSpacing: ".08em",
    sectionPadding: "13px 22px 9px",
    rowPadding: "11px 22px",
    rowGap: "12px",
    rowAvatarSize: 26,
    rowAvatarFontSize: "9.5px",
    rowNameFontSize: "12.5px",
    rowContactFontSize: "11px",
    reasonFontSize: "11px",
    reasonPadding: "3px 9px",
    footerPadding: "15px 22px",
    footerGap: "10px",
    footerNoteFontSize: "11.5px",
    footerButtonHeight: 34,
    footerButtonRadius: "8px",
    footerButtonPaddingX: "15px",
    footerButtonFontSize: "12px",

    /** The filtered worklist — what "Retry failed" lands on. */
    filtersPadding: "14px 22px",
    filtersGap: "8px",
    filterHeight: 32,
    filterPaddingX: "13px",
    filterRadius: "999px",
    filterBorderWidth: "1.5px",
    filterFontSize: "12px",
    filterCountFontSize: "11px",
    tableColumns: "minmax(0,1fr) minmax(0,1.4fr) 156px 140px",
    tableHeaderPadding: "13px 22px",
    tableHeaderFontSize: "11.5px",
    tableHeaderLetterSpacing: ".06em",
    tableRowPadding: "14px 22px",
    tableRowGap: "18px",
    tableAvatarSize: 30,
    tableAvatarFontSize: "10px",
    tableNameFontSize: "13px",
    tableMetaFontSize: "11.5px",
    correctionHeight: 36,
    correctionRadius: "9px",
    correctionFontSize: "12.5px",
    actionHeight: 32,
    actionRadius: "8px",
    actionFontSize: "11.5px",
    notePadding: "15px 22px",
  },

  /** Batches grouped in the Sent list, each expanding to its own envelopes. */
  batchList: {
    rowPadding: "15px 22px",
    rowGap: "13px",
    chevronSize: 11,
    badgeHeight: 22,
    badgePaddingX: "8px",
    badgeRadius: "6px",
    badgeFontSize: "9.5px",
    badgeLetterSpacing: ".04em",
    nameFontSize: "13.5px",
    metaFontSize: "11.5px",
    metaMarginTop: "2px",
    miniBarWidth: 150,
    miniBarHeight: 8,
    miniBarRadius: "999px",
    statusWidth: 96,
    statusFontSize: "12px",
    childrenPadding: "0 22px 12px 60px",
    childRowPadding: "10px 0",
    childGap: "12px",
    childAvatarSize: 24,
    childAvatarFontSize: "9px",
    childNameFontSize: "12.5px",
    childWhenFontSize: "11.5px",
    childStatusFontSize: "11.5px",
    seeAllHeight: 38,
    seeAllFontSize: "12px",
  },

  /** Phone frame values, from the mobile artboards. */
  mobile: {
    gutter: "14px",
    bodyPaddingTop: "18px",
    bodyPaddingBottom: "120px",
    bodyGap: "14px",
    titleFontSize: "19px",
    subFontSize: "12.5px",
    stepLabelFontSize: "10.5px",
    stepNameFontSize: "11.5px",
    stepBarHeight: 4,
    stepBarGap: "4px",
    cardRadius: "13px",
    cardPadding: "16px",
    cardGap: "13px",
    iconSize: 38,
    iconRadius: "11px",
    inputHeight: 42,
    toggleItemHeight: 32,
    footerPadding: "12px 14px 16px",
    footerGap: "10px",
    footerButtonHeight: 48,
    footerButtonRadius: "11px",
    footerButtonFontSize: "14px",
  },
} as const;

export default bulkSendTokens;
